// Wrapper fino sobre `expo-brightness` para a tela de Sessão (Etapa 12 —
// spec S03 §Tela e energia). Import de módulo nativo — assim como
// expo-image-picker/expo-file-system em
// src/ui/projects/ProjectPhotosSection.tsx, este arquivo não tem teste
// automatizado (o binding nativo só existe rodando num device/simulador de
// verdade; fora disso `NativeModulesProxy` não existe — ver aviso no
// console dos testes que tocam src/db/client.ts). A lógica de ESTADO (qual
// valor é o "original" pendente de restauração, sobrevivendo a kill) fica
// em brightnessFlag.ts (AsyncStorage puro), que É testado.
import { Platform } from 'react-native';
import * as Brightness from 'expo-brightness';
import { markBrightnessDirty, getDirtyBrightness, clearBrightnessDirty } from './brightnessFlag';

/** Sem toque na tela por este tempo (com o cronômetro rodando) → reduz o brilho. */
export const SESSION_DIM_TIMEOUT_MS = 30_000;
/** Nível de brilho reduzido (0–1, ver expo-brightness). */
export const SESSION_DIM_BRIGHTNESS = 0.1;
/** Tag do `expo-keep-awake` — isolada por nome para só afetar esta tela
 * (ativar/desativar aqui nunca libera um `activateKeepAwakeAsync` de outra
 * parte do app que porventura use a tag default). */
export const SESSION_KEEP_AWAKE_TAG = 'crochet-session';

/**
 * Reduz o brilho da tela para `SESSION_DIM_BRIGHTNESS`, primeiro
 * capturando e persistindo (`markBrightnessDirty`) o valor ORIGINAL — o
 * chamador (SessionScreen) é responsável por só invocar isto UMA VEZ por
 * visita à tela (guarda um ref local "já reduzi?") para nunca recapturar o
 * brilho já reduzido como se fosse o original. Devolve o valor original
 * capturado, para o chamador guardar em memória e restaurar depois sem
 * precisar reler o AsyncStorage a cada toque.
 */
export async function dimScreenBrightness(): Promise<number> {
  if (Platform.OS === 'web') return 1;
  const original = await Brightness.getBrightnessAsync();
  await markBrightnessDirty(original);
  await Brightness.setBrightnessAsync(SESSION_DIM_BRIGHTNESS);
  return original;
}

/** Restaura o brilho para o valor original (já conhecido em memória pelo
 * chamador nesta visita à tela) e limpa a flag de "sujo" — usado ao sair
 * normalmente (touch/pausar/desmontar/background). */
export async function restoreScreenBrightness(originalBrightness: number): Promise<void> {
  if (Platform.OS === 'web') return;
  await Brightness.setBrightnessAsync(originalBrightness);
  await clearBrightnessDirty();
}

/**
 * Restaura um brilho "sujo" deixado por uma visita ANTERIOR à tela de
 * Sessão que não conseguiu se restaurar sozinha (app morto com o brilho
 * ainda reduzido) — não depende de nenhum estado em memória desta
 * execução, só da flag persistida (brightnessFlag.ts). Chamado no boot do
 * app (app/_layout.tsx) e, defensivamente, também ao montar a tela de
 * Sessão de novo.
 */
export async function restorePendingBrightnessIfDirty(): Promise<void> {
  if (Platform.OS === 'web') return;
  const dirty = await getDirtyBrightness();
  if (dirty === null) return;
  await Brightness.setBrightnessAsync(dirty);
  await clearBrightnessDirty();
}
